import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Trophy, Play, RotateCw, CheckCircle2, X, Crown, ShoppingCart, Loader2 } from "lucide-react";
import Btn from "./Btn";
import api, { apiErr } from "../lib/api";

const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

function LeaderboardModal({ pack, slot, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/tryout-packs/${pack.pack_product_id}/slot/${slot}/leaderboard`)
      .then(({ data }) => setRows(data.leaderboard)).catch((e) => { toast.error(apiErr(e)); setRows([]); });
  }, [pack, slot]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="pack-leaderboard-modal">
      <div className="card-chunky p-6 w-full max-w-md max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-black text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-su-secondary" /> Peringkat Tryout #{slot}</h3>
          <button onClick={onClose} data-testid="pack-lb-close" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-su-muted font-medium text-sm mb-4 truncate">{pack.title}</p>
        {rows === null ? <div className="grid place-items-center py-10"><Loader2 className="w-8 h-8 animate-spin text-su-primary" /></div>
          : rows.length === 0 ? <p className="text-su-muted text-sm text-center py-8">Belum ada peserta lain di tryout ini.</p>
          : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.rank} className={`flex items-center gap-3 rounded-2xl p-3 border-2 ${r.is_me ? "border-su-primary bg-su-primary/5" : "border-su-border"}`}>
                  <span className={`w-8 h-8 grid place-items-center rounded-lg font-heading font-black text-sm shrink-0 ${r.rank === 1 ? "bg-su-secondary text-black" : r.rank === 2 ? "bg-slate-300 text-black" : r.rank === 3 ? "bg-amber-700 text-white" : "bg-su-track text-su-muted"}`}>{r.rank}</span>
                  <span className="flex-1 font-heading font-extrabold truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}{r.is_me && <span className="text-xs text-su-primary">(kamu)</span>}</span>
                  <span className="font-mono font-bold text-su-primary shrink-0">{r.score}</span>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export default function TryoutPacks() {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(null);
  const [lb, setLb] = useState(null); // {pack, slot}
  const nav = useNavigate();

  const load = () => api.get("/tryout-packs").then(({ data }) => setData(data)).catch(() => setData({ catalog: [], owned: [] }));
  useEffect(() => { load(); }, []);

  const startSlot = async (pack) => {
    setStarting(pack.pack_product_id);
    try {
      const { data: res } = await api.post(`/tryout-packs/${pack.pack_product_id}/start`);
      nav(`/cbt/${res.attempt_id}`, { state: res });
    } catch (e) { toast.error(apiErr(e)); setStarting(null); }
  };

  if (!data) return null;
  const { catalog = [], owned = [] } = data;
  const ownedIds = new Set(owned.map((o) => o.pack_product_id));
  const buyable = catalog.filter((c) => !ownedIds.has(c.product_id));

  if (owned.length === 0 && buyable.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="tryout-packs">
      <div className="flex items-center gap-2">
        <Package className="w-6 h-6 text-su-secondaryShadow" />
        <h2 className="font-heading font-black text-xl">Paket Tryout</h2>
      </div>

      {owned.map((pack) => {
        const next = pack.slots.find((s) => s.status !== "done");
        return (
          <div key={pack.pack_product_id} className="card-chunky p-6" data-testid={`owned-pack-${pack.pack_product_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="font-heading font-black text-lg">{pack.title}</h3>
                <p className="text-su-muted font-bold text-sm">{pack.remaining} dari {pack.total_slots} tryout tersisa · tanpa token/energi</p>
              </div>
              {pack.exam && <span className="text-[10px] font-heading font-black uppercase px-2.5 py-1 rounded-full bg-su-primary/15 text-su-primary">{pack.exam}</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {pack.slots.map((s) => (
                <div key={s.slot} className="flex items-center gap-3 rounded-2xl border-2 border-su-border p-3">
                  <span className="w-9 h-9 grid place-items-center rounded-xl bg-su-track font-heading font-black shrink-0">#{s.slot}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-extrabold text-sm">Tryout #{s.slot}</p>
                    {s.status === "done"
                      ? <p className="text-xs font-bold text-su-success flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Skor {s.score} · Peringkat #{s.rank}/{s.total}</p>
                      : s.status === "in_progress"
                        ? <p className="text-xs font-bold text-su-energy">Sedang berjalan</p>
                        : <p className="text-xs font-medium text-su-muted">Belum dikerjakan</p>}
                  </div>
                  {s.status === "done"
                    ? <Btn size="sm" variant="ghost" data-testid={`pack-rank-${pack.pack_product_id}-${s.slot}`} onClick={() => setLb({ pack, slot: s.slot })}><Trophy className="w-4 h-4" /> Peringkat</Btn>
                    : (next && next.slot === s.slot)
                      ? <Btn size="sm" data-testid={`pack-start-${pack.pack_product_id}-${s.slot}`} disabled={starting === pack.pack_product_id} onClick={() => startSlot(pack)}>
                          {starting === pack.pack_product_id ? "..." : (s.status === "in_progress" ? <><RotateCw className="w-4 h-4" /> Lanjut</> : <><Play className="w-4 h-4" /> Kerjakan</>)}
                        </Btn>
                      : <span className="text-xs text-su-muted font-bold pr-1">Menunggu</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {buyable.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          {buyable.map((p) => (
            <div key={p.product_id} className="card-chunky p-5 flex flex-col" data-testid={`pack-buy-${p.product_id}`}>
              <h3 className="font-heading font-black">{p.name}</h3>
              <p className="font-heading font-black text-2xl text-su-primary my-1">{fmt(p.amount)}</p>
              <ul className="text-xs font-medium text-su-muted space-y-1 my-3 flex-1">
                {(p.features || []).map((x) => <li key={x} className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-su-success shrink-0 mt-0.5" />{x}</li>)}
              </ul>
              <Btn variant="secondary" className="w-full" data-testid={`pack-goto-buy-${p.product_id}`} onClick={() => nav("/pricing")}><ShoppingCart className="w-4 h-4" /> Beli Paket</Btn>
            </div>
          ))}
        </div>
      )}

      {lb && <LeaderboardModal pack={lb.pack} slot={lb.slot} onClose={() => setLb(null)} />}
    </div>
  );
}
